import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Tab, Tabs, Typography, Grid, Button, CircularProgress } from '@mui/material';
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import PlayerRankMockCard from "../../components/PlayerRankMockCard/PlayerRankMockCard";
import MaterialReactTable from 'material-react-table';
import moment from 'moment';
import PlayerDraftOdd from "../../components/PlayerDraftOdd/PlayerDraftOdd";
import { publicApi } from "../../api/http";
import { authAxios, authRequestHeaders, useAuth } from "../../contexts/AuthContext";
import PlayerCard from "../../components/PlayerCard/PlayerCard";
import PlayerGraph from "../../components/PlayerGraph/PlayerGraph";
import { Container } from "@mui/system";
import { goalieColumns, playerColumns } from "./Constants.js";
import PlayerDiscussionTab from "./PlayerDiscussionTab";
import PlayerCompareTab from "./PlayerCompareTab";
import PlayerDraftOddsTab from "./PlayerDraftOddsTab";
import PlayerScoutingReportTab from "./PlayerScoutingReportTab";
import PlayerShareModal from "../../components/PlayerShareModal/PlayerShareModal";
import {
    DEFAULT_PLAYER_TAB,
    buildPlayerPageUrl,
    getPlayerTabMeta,
    normalizePlayerTab,
} from "./playerTabs";

import "./Player.css"

const YOUTUBE_THUMB_QUALITIES = [
    { key: "max", file: "maxresdefault.jpg" },
    { key: "hq", file: "hqdefault.jpg" },
    { key: "mq", file: "mqdefault.jpg" },
];

function getYouTubeThumbnailUrl(videoId, qualityIndex = 0) {
    if (!videoId) return "";
    const quality = YOUTUBE_THUMB_QUALITIES[Math.min(qualityIndex, YOUTUBE_THUMB_QUALITIES.length - 1)];
    return `https://i.ytimg.com/vi/${videoId}/${quality.file}`;
}

function FilmThumbnail({ videoId, alt, className, startQualityIndex = 2 }) {
    const [qualityIndex, setQualityIndex] = useState(startQualityIndex);

    useEffect(() => {
        setQualityIndex(startQualityIndex);
    }, [videoId, startQualityIndex]);

    if (!videoId) return null;

    const handleError = () => {
        setQualityIndex((current) => (
            current < YOUTUBE_THUMB_QUALITIES.length - 1 ? current + 1 : current
        ));
    };

    return (
        <img
            src={getYouTubeThumbnailUrl(videoId, qualityIndex)}
            alt={alt}
            className={className}
            onError={handleError}
            loading="lazy"
        />
    );
}

function Player() {
    const { user, loading: authLoading, ensureAccessToken } = useAuth();
    const [playerData, setPlayerData] = useState(null);
    const [playerStats, setPlayerStats] = useState(null);

    const [graphData, setGraphData] = useState(null);
    const [lastConsensusData, setLastConsensusData] = useState(null);
    const [playerRankMocks, setPlayerRankMocks] = useState(null);
    const [playerDraftProbabilities, setPlayerDraftProbabilities] = useState([]);
    const [playerFilm, setPlayerFilm] = useState([]);
    const [activeFilmKey, setActiveFilmKey] = useState("");
    const [playingFilmKey, setPlayingFilmKey] = useState("");
    const [playerCardHeight, setPlayerCardHeight] = useState(null);
    const playerCardRef = useRef(null);
    const { player_id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [watchlistLoading, setWatchlistLoading] = useState(false);
    const [isOnWatchlist, setIsOnWatchlist] = useState(false);
    const [discussionCount, setDiscussionCount] = useState(0);
    const [scoutingReportCount, setScoutingReportCount] = useState(0);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    useEffect(() => {
        if (!player_id) {
            setDiscussionCount(0);
            return;
        }
        publicApi.get(`/discussion/player/${player_id}`)
            .then((response) => {
                setDiscussionCount(Array.isArray(response.data) ? response.data.length : 0);
            })
            .catch(() => setDiscussionCount(0));
    }, [player_id]);

    useEffect(() => {
        if (!player_id) {
            setScoutingReportCount(0);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const token = await ensureAccessToken();
                const res = token
                    ? await authAxios.get(`/scouting_reports/player/${player_id}`, {
                        headers: authRequestHeaders(token),
                    })
                    : await publicApi.get(`/scouting_reports/player/${player_id}`);
                if (!cancelled) {
                    setScoutingReportCount(Array.isArray(res.data) ? res.data.length : 0);
                }
            } catch {
                if (!cancelled) setScoutingReportCount(0);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [player_id, ensureAccessToken, authLoading]);

    useEffect(() => {
        publicApi.get("/players/" + player_id).then((response) => {
            setPlayerData(response.data);
        })
            .catch((error) => {
                console.error(error);
                setPlayerData([]);
            });
    }, [player_id]);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !player_id) {
            setIsOnWatchlist(false);
            return;
        }

        let cancelled = false;
        (async () => {
            setWatchlistLoading(true);
            try {
                const token = await ensureAccessToken();
                if (cancelled) return;
                if (!token) {
                    setIsOnWatchlist(false);
                    return;
                }
                const res = await authAxios.get("/watchlist", { headers: authRequestHeaders(token) });
                if (cancelled) return;
                const items = Array.isArray(res.data) ? res.data : [];
                setIsOnWatchlist(items.some((p) => p?.player_id === player_id));
            } catch (err) {
                if (!cancelled) {
                    console.warn("[watchlist] load failed", err.response?.status, err.response?.data);
                    setIsOnWatchlist(false);
                }
            } finally {
                if (!cancelled) setWatchlistLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authLoading, user, player_id, ensureAccessToken]);

    const toggleWatchlist = async () => {
        if (watchlistLoading || !user || !player_id) return;
        setWatchlistLoading(true);
        try {
            const token = await ensureAccessToken();
            if (!token) {
                setIsOnWatchlist(false);
                return;
            }
            if (isOnWatchlist) {
                await authAxios.delete(`/watchlist/${player_id}`, { headers: authRequestHeaders(token) });
                setIsOnWatchlist(false);
            } else {
                await authAxios.post(`/watchlist/${player_id}`, null, { headers: authRequestHeaders(token) });
                setIsOnWatchlist(true);
            }
        } catch (err) {
            console.warn("[watchlist] toggle failed", err.response?.status, err.response?.data);
        } finally {
            setWatchlistLoading(false);
        }
    };

    useEffect(() => {
        publicApi.get("/player_stats/" + player_id).then((response) => {
            setPlayerStats(response.data);
        })
            .catch((error) => {
                console.error(error);
                setPlayerStats([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/player_draft_probabilities/player/" + player_id).then((response) => {
            setPlayerDraftProbabilities(response.data);
        })
            .catch((error) => {
                console.error(error);
                setPlayerDraftProbabilities([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/film/" + player_id).then((response) => {
            setPlayerFilm(Array.isArray(response.data) ? response.data : []);
        })
            .catch((error) => {
                console.error(error);
                setPlayerFilm([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/shared/get_player_mocks_and_rankings/" + player_id).then((response) => {
            setPlayerRankMocks(response.data);
        })
            .catch((error) => {
                console.error(error);
                setPlayerRankMocks([]);
            });
    }, [player_id]);

    useEffect(() => {
        publicApi.get("/consensus_rankings/" + player_id).then((response) => {
            setLastConsensusData(response.data[response.data.length - 1]);
            return response.data
        }).then((re) => {
            const scores = re.map(item => Math.round(item.score * 1e2) / 1e2);
            const effectiveDates = re.map(item => moment(item.effective_date).valueOf());
            const lastRank = re.map(item => ((item.has_data_point) ? item.last_rank : null));
            const consensusRanking = re.map(item => item.rank);
            const lastRankType = re.map(item => item.last_rank_type);

            // Create an array of objects combining the effective date, score, and rank
            setGraphData(re.map((item, index) => ({
                effective_date: effectiveDates[index],
                score: scores[index],
                lastRank: lastRank[index],
                consensusRanking: consensusRanking[index],
                lastRankType: lastRankType[index]
            })));
        })
            .catch((error) => {
                console.error(error);
            });
    }, [player_id]);

    let darkTheme = createTheme({
        palette: {
            mode: 'dark',
            info: {
                main: '#7ea862',
                secondary: '#7ea862',
            },
            background: {
                default: '#14181d',
                paper: '#14181d',
            },
            text: {
                primary: '#f3f4f6',
                secondary: '#a1a8b3',
            },
        },
        shadows: ["none"]
    });

    const playerColumnsMemo = useMemo(
        () => playerColumns, [],
    );

    const goalieColumnsMemo = useMemo(
        () => goalieColumns, [],
    );

    // e.g. draft_year=2026 → "2025-26" (regular season + playoffs)
    const filteredPlayerStats = useMemo(() => {
        if (!playerStats || !playerData?.draft_year) return playerStats;
        const draftYear = playerData.draft_year;
        const targetSeason = `${draftYear - 1}-${String(draftYear).slice(-2)}`;
        return playerStats.filter(stat => stat.year === targetSeason);
    }, [playerStats, playerData]);

    const hasFilm = playerFilm && playerFilm.length > 0;

    const activeTab = useMemo(
        () => normalizePlayerTab(searchParams.get("tab"), { hasFilm }),
        [searchParams, hasFilm]
    );

    const tabMeta = useMemo(
        () => (playerData ? getPlayerTabMeta(activeTab, playerData) : null),
        [activeTab, playerData]
    );

    const playerPageUrl = useMemo(
        () => buildPlayerPageUrl(player_id, activeTab),
        [player_id, activeTab]
    );

    const handleTabChange = (event, value) => {
        if (value === DEFAULT_PLAYER_TAB) {
            setSearchParams({}, { replace: true });
            return;
        }
        setSearchParams({ tab: value }, { replace: true });
    };

    useEffect(() => {
        const rawTab = searchParams.get("tab");
        if (!rawTab) return;

        const normalized = normalizePlayerTab(rawTab, { hasFilm });
        if (rawTab === normalized) return;

        if (normalized === DEFAULT_PLAYER_TAB) {
            setSearchParams({}, { replace: true });
        } else {
            setSearchParams({ tab: normalized }, { replace: true });
        }
    }, [hasFilm, searchParams, setSearchParams]);

    const getFilmKey = (film) => film?.id || film?.url || "";

    const getYouTubeVideoId = (url) => {
        if (!url) return "";

        try {
            const parsedUrl = new URL(url);
            const videoId = parsedUrl.searchParams.get("v");
            if (videoId) {
                return videoId;
            }

            const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
            if (parsedUrl.hostname.includes("youtu.be")) {
                return pathParts[0] || "";
            }
            if (pathParts[0] === "embed" || pathParts[0] === "shorts") {
                return pathParts[1] || "";
            }
        } catch {
            const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([^&?/]+)/);
            return match?.[1] || "";
        }

        return "";
    };

    useEffect(() => {
        if (!hasFilm) {
            setActiveFilmKey("");
            return;
        }

        if (!playerFilm.some((film) => getFilmKey(film) === activeFilmKey)) {
            setActiveFilmKey(getFilmKey(playerFilm[0]));
        }
    }, [activeFilmKey, hasFilm, playerFilm]);

    useEffect(() => {
        setPlayingFilmKey("");
    }, [activeFilmKey]);

    useEffect(() => {
        if (!playerCardRef.current) {
            return undefined;
        }

        const updatePlayerCardHeight = () => {
            const nextHeight = playerCardRef.current?.getBoundingClientRect().height;
            if (nextHeight) {
                setPlayerCardHeight(nextHeight);
            }
        };

        updatePlayerCardHeight();

        if (!window.ResizeObserver) {
            window.addEventListener("resize", updatePlayerCardHeight);
            return () => window.removeEventListener("resize", updatePlayerCardHeight);
        }

        const observer = new ResizeObserver(updatePlayerCardHeight);
        observer.observe(playerCardRef.current);
        return () => observer.disconnect();
    }, [playerData]);

    const getYouTubeEmbedUrl = (url, { autoplay = false } = {}) => {
        if (!url) return "";
        const videoId = getYouTubeVideoId(url);
        if (!videoId) return url;
        const base = `https://www.youtube.com/embed/${videoId}`;
        return autoplay ? `${base}?autoplay=1` : base;
    };

    const activeFilm = useMemo(
        () => playerFilm.find((film) => getFilmKey(film) === activeFilmKey) || playerFilm[0] || null,
        [activeFilmKey, playerFilm]
    );

    return (
        <div>
            {playerData && tabMeta && (
                <Helmet>
                    <title>{tabMeta.title}</title>
                    <meta name="description" content={tabMeta.description} />
                    <link rel="canonical" href={playerPageUrl} />
                    <meta property="og:title" content={tabMeta.title} />
                    <meta property="og:description" content={tabMeta.description} />
                    <meta property="og:url" content={playerPageUrl} />
                </Helmet>
            )}
            {/* PLAYER CARD AND TABBED SUMMARY */}
            <Container style={{ marginTop: 16 }}>
                {playerData && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            onClick={() => setShareModalOpen(true)}
                            variant="outlined"
                            disableElevation
                            startIcon={<ShareOutlinedIcon />}
                            sx={{
                                textTransform: "none",
                                py: 0.6,
                                px: 1.25,
                                minHeight: 34,
                                borderRadius: 2,
                                color: "var(--text-primary)",
                                borderColor: "var(--border-strong)",
                                backgroundColor: "var(--bg-surface)",
                                "&:hover": {
                                    borderColor: "rgba(148, 192, 115, 0.65)",
                                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                                },
                            }}
                        >
                            Share
                        </Button>
                        {user && (
                            <Button
                                onClick={toggleWatchlist}
                                disabled={watchlistLoading}
                                variant={isOnWatchlist ? "outlined" : "contained"}
                                disableElevation
                                startIcon={isOnWatchlist ? <StarRoundedIcon /> : <StarBorderRoundedIcon />}
                                sx={{
                                    textTransform: "none",
                                    py: 0.6,
                                    px: 1.25,
                                    minHeight: 34,
                                    borderRadius: 2,
                                    ...(isOnWatchlist
                                        ? {
                                            color: "var(--brand-strong)",
                                            borderColor: "var(--brand-strong)",
                                            backgroundColor: "transparent",
                                            "&:hover": {
                                                borderColor: "var(--brand-strong)",
                                                backgroundColor: "var(--brand-soft)",
                                            },
                                        }
                                        : {
                                            color: "#0b0d10",
                                            background: "linear-gradient(145deg, var(--brand-strong), var(--brand))",
                                            "&:hover": {
                                                background: "linear-gradient(145deg, var(--brand), var(--brand-strong))",
                                            },
                                        }),
                                    "&.Mui-disabled": {
                                        opacity: 0.65,
                                    },
                                }}
                            >
                                {watchlistLoading ? (
                                    <CircularProgress size={18} />
                                ) : isOnWatchlist ? (
                                    "Remove from watchlist"
                                ) : (
                                    "Add to watchlist"
                                )}
                            </Button>
                        )}
                    </Box>
                )}
            </Container>
            <PlayerShareModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                player={playerData}
                activeTab={activeTab}
            />

            <Container style={{ marginTop: 16 }}>
                <Grid container spacing={2} className="player-hero-grid">
                    <Grid item xs={12} sm={12} md={3} className="player-card-column">
                        <div className="player-card-measure" ref={playerCardRef}>
                            {playerData && <PlayerCard playerData={playerData} />}
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={9} className="player-tabs-column">
                        <div
                            className="container player-tabs-container"
                            style={playerCardHeight ? { "--player-card-height": `${playerCardHeight}px` } : undefined}
                        >
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                className="player-tabs"
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                <Tab label="Consensus" value="consensus" />
                                <Tab label="Draft Odds" value="draft-odds" />
                                <Tab label="Stats" value="stats" />
                                {hasFilm && <Tab label="Film" value="film" />}
                                <Tab label="Compare" value="compare" />
                                <Tab label={`Scouting Reports [${scoutingReportCount}]`} value="scouting-reports" />
                                <Tab label={`Discussion [${discussionCount}]`} value="discussion" />
                            </Tabs>

                            <Box className="player-tab-panel" hidden={activeTab !== "consensus"}>
                                {lastConsensusData && (
                                    <>
                                        {graphData && <PlayerGraph graphData={graphData} />}
                                        <Grid container spacing={2} className="rank-stat-grid">
                                            <Grid item xs={6} sm={6} md={12 / 5} >
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Number of Rankings:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {lastConsensusData.count}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={12 / 5}>
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Highest Ranking:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {lastConsensusData.best_rank}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={12 / 5}>
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Lowest Ranking:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {lastConsensusData.worst_rank}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={12 / 5}>
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Last Ranking:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {lastConsensusData.last_rank}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={12 / 5}>
                                                <div className="rank-stat">
                                                    <Typography variant="h4" component="div" className="rank-stat-title">
                                                        Standard Deviation:
                                                    </Typography>
                                                    <Typography variant="h4" component="div" className="rank-stat-data">
                                                        {Math.round(lastConsensusData.std_dev * 1e2) / 1e2}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                                {!lastConsensusData && (
                                    <Typography variant="body1" component="div" className="empty-tab-message">
                                        No consensus ranking history available for this player.
                                    </Typography>
                                )}
                            </Box>

                            <Box className="player-tab-panel" hidden={activeTab !== "stats"}>
                                <Typography variant="h4" component="div" className="tab-section-title">
                                    Draft Year Stats
                                </Typography>
                                {playerData && filteredPlayerStats && (
                                    <>
                                        <ThemeProvider theme={darkTheme}>
                                            <MaterialReactTable
                                                columns={(playerData.position === "G") ? goalieColumnsMemo : playerColumnsMemo}
                                                data={filteredPlayerStats}
                                                initialState={{ density: 'compact', pagination: { pageSize: 10 } }}
                                            />
                                        </ThemeProvider>
                                        {filteredPlayerStats.length === 0 && (
                                            <Typography variant="body1" component="div" className="no-stats-message">
                                                No stats recorded for the player's draft year.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>

                            <PlayerScoutingReportTab
                                playerId={player_id}
                                playerData={playerData}
                                isActive={activeTab === "scouting-reports"}
                                onReportCountChange={setScoutingReportCount}
                            />

                            <PlayerDiscussionTab
                                playerId={player_id}
                                isActive={activeTab === "discussion"}
                                onCommentCountChange={setDiscussionCount}
                            />

                            <PlayerDraftOddsTab
                                playerId={player_id}
                                draftYear={playerData?.draft_year}
                                draftResults={playerData?.draft_results}
                                isActive={activeTab === "draft-odds"}
                            />

                            <PlayerCompareTab
                                playerId={player_id}
                                playerData={playerData}
                                isActive={activeTab === "compare"}
                            />

                            {hasFilm && (
                                <Box className="player-tab-panel film-tab-panel" hidden={activeTab !== "film"}>
                                    <Grid container spacing={2} className="film-browser">
                                        <Grid item xs={12} md={3} className="film-list-column">
                                            <div className="film-list">
                                                {playerFilm.map((film) => {
                                                    const filmKey = getFilmKey(film);
                                                    const videoId = getYouTubeVideoId(film.url);
                                                    const isActiveFilm = getFilmKey(activeFilm) === filmKey;

                                                    return (
                                                        <button
                                                            type="button"
                                                            key={filmKey}
                                                            className={`film-list-item${isActiveFilm ? " film-list-item--active" : ""}`}
                                                            onClick={() => setActiveFilmKey(filmKey)}
                                                        >
                                                            {videoId && (
                                                                <FilmThumbnail
                                                                    videoId={videoId}
                                                                    alt=""
                                                                    className="film-list-thumbnail"
                                                                    startQualityIndex={1}
                                                                />
                                                            )}
                                                            <span className="film-list-title">
                                                                {film.title || "Player Film"}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} md={9} className="film-active-column">
                                            {activeFilm && (
                                                <div className="film-active-card">
                                                    <div className="film-active-frame">
                                                        {playingFilmKey === getFilmKey(activeFilm) ? (
                                                            <iframe
                                                                width="100%"
                                                                height="100%"
                                                                src={getYouTubeEmbedUrl(activeFilm.url, { autoplay: true })}
                                                                title={activeFilm.title || "Player film"}
                                                                frameBorder="0"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                allowFullScreen
                                                            />
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="film-active-poster"
                                                                onClick={() => setPlayingFilmKey(getFilmKey(activeFilm))}
                                                                aria-label={`Play ${activeFilm.title || "player film"}`}
                                                            >
                                                                <FilmThumbnail
                                                                    videoId={getYouTubeVideoId(activeFilm.url)}
                                                                    alt=""
                                                                    className="film-active-poster-image"
                                                                    startQualityIndex={0}
                                                                />
                                                                <span className="film-active-play-icon" aria-hidden="true" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <Typography variant="body1" component="div" className="film-title">
                                                        {activeFilm.title || "Player Film"}
                                                    </Typography>
                                                </div>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </div>
                    </Grid>
                </Grid>
            </Container>

            {playerDraftProbabilities && playerDraftProbabilities.length > 0 && <Container >
                <Grid container columnSpacing={2} style={{ marginTop: 20 }}>
                    {playerDraftProbabilities && <Grid item xs={12} sm={12} md={12}>
                        <div className="container con-no-bot-pad player-draft-odds">
                            <div className="draft-odds">
                                <Typography variant="h4" component="div" className="draft-odds-title">
                                    Draft Odds
                                </Typography>
                                <Grid container columnSpacing={0}>
                                    <Grid item xs={12} sm={12} md={4}>
                                        {playerDraftProbabilities && playerDraftProbabilities.slice(0, playerDraftProbabilities.length / 3)
                                            .map((draftProb) => (
                                                <PlayerDraftOdd draftProb={draftProb} />
                                            ))
                                        }
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={4}>
                                        {playerDraftProbabilities && playerDraftProbabilities.slice(playerDraftProbabilities.length / 3, (playerDraftProbabilities.length / 3) * 2)
                                            .map((draftProb) => (
                                                <PlayerDraftOdd draftProb={draftProb} />
                                            ))
                                        }
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={4}>
                                        {playerDraftProbabilities && playerDraftProbabilities.slice((playerDraftProbabilities.length / 3) * 2, playerDraftProbabilities.length)
                                            .map((draftProb) => (
                                                <PlayerDraftOdd draftProb={draftProb} />
                                            ))
                                        }
                                    </Grid>
                                </Grid>
                            </div>
                        </div>
                    </Grid>}
                </Grid>
            </Container>}


            {/* PLAYER RANKINGS */}
            <Container spacing={2} style={{ marginTop: 40 }} className="container" >
                <Grid container >
                    <Grid item xs={12} sm={12} md={12} >
                        <Typography variant="h4" component="div" className="page-title">
                            All Rankings
                        </Typography>
                        <div className="title-underline" />
                    </Grid>
                    {playerRankMocks && playerRankMocks.map((playerRankMock) => (
                        <Grid item xs={12} sm={12} md={12} >
                            <PlayerRankMockCard playerRankMock={playerRankMock} />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </div>
    );
};

export default Player;
